import { clsx } from 'clsx'
import { CSSProperties, PropsWithChildren, useMemo } from 'react'
import { useUiKit } from '../providers'

export type TextType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'subtitle'
  | 'subtitle-semibold'
  | 'body1-regular'
  | 'body1-semibold'
  | 'body2-regular'
  | 'body3-regular'

interface TextProps extends PropsWithChildren {
  id?: string
  type?: TextType
  color?: string
  onClick?: () => void
  className?: string
  style?: CSSProperties
}

/**
 * @deprecated Use the className prop instead.
 */
export function Text({
  id,
  type = 'body1-regular',
  color,
  onClick,
  className,
  style,
  children
}: TextProps): JSX.Element {
  const { isMobile } = useUiKit()

  const styles = useMemo((): CSSProperties => {
    const result: CSSProperties = { color }

    switch (type) {
      case 'h1':
        result.fontFamily = 'safiromedium'
        result.fontWeight = 500
        result.fontSize = isMobile ? '64px' : '120px'
        result.lineHeight = isMobile ? '68px' : '108px'
        result.letterSpacing = '-0.03em'
        break
      case 'h2':
        result.fontSize = isMobile ? '40px' : '48px'
        result.lineHeight = isMobile ? '48px' : '48px'
        result.fontWeight = 600
        result.letterSpacing = '-0.02em'
        break
      case 'h3':
        result.fontSize = isMobile ? '32px' : '40px'
        result.lineHeight = isMobile ? '40px' : '48px'
        result.fontWeight = 600
        result.letterSpacing = '-0.02em'
        break
      case 'h4':
        result.fontSize = '32px'
        result.lineHeight = '40px'
        result.fontWeight = 600
        result.letterSpacing = '-0.02em'
        break
      case 'h5':
        result.fontSize = '24px'
        result.lineHeight = '100%'
        result.fontWeight = 600
        result.letterSpacing = '-0.02em'
        break
      case 'subtitle':
        result.fontSize = '20px'
        result.lineHeight = '28px'
        result.letterSpacing = '-0.01em'
        break
      case 'subtitle-semibold':
        result.fontSize = '20px'
        result.lineHeight = '28px'
        result.fontWeight = 600
        result.letterSpacing = '-0.01em'
        break
      case 'body1-regular':
      default:
        result.fontSize = '16px'
        result.lineHeight = '24px'
        break
      case 'body1-semibold':
        result.fontWeight = 600
        result.fontSize = '16px'
        result.lineHeight = '24px'
        break
      case 'body2-regular':
        result.fontSize = '14px'
        result.lineHeight = '18px'
        break
      case 'body3-regular':
        result.fontSize = '12px'
        result.lineHeight = '16px'
        break
    }

    return {
      ...result,
      ...(style || {})
    }
  }, [isMobile, color, style, type])

  return (
    <span id={id} className={clsx('transition-colors', className)} style={styles} onClick={onClick}>
      {children}
    </span>
  )
}
